import { useState, useEffect, useMemo, useRef } from "react";
import { listUsers } from "../../../../services/adminUsersService";
import { UserFilters, UserListResponse, AdminUser } from "../../../../interfaces/adminUsers";
import { useUrlState } from "./useUrlState";
import { shallowEqual } from "../../../../utils/shallowEqual";

interface UseUsersQueryReturn {
  data: UserListResponse<AdminUser> | null;
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  params: UserFilters;
  setParams: (patch: Partial<UserFilters>) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  refetch: () => void;
}

/**
 * Hook for managing users data with URL-driven state
 * Reads params from URL, calls listUsers service, and provides data + controls
 */
export function useUsersQuery(): UseUsersQueryReturn {
  const { params, setParams, clearFilters, hasActiveFilters } = useUrlState();
  const [data, setData] = useState<UserListResponse<AdminUser> | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Ref to track in-flight request key
  const inflightRef = useRef<string | null>(null);
  // State-based trigger for manual refetch (state causes re-render, unlike ref)
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0);

  // Memoize params to ensure stable dependency
  const stableParams = useMemo(
    () => ({
      page: params.page,
      limit: params.limit,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
      q: params.q,
      role: params.role,
      status: params.status,
      verified: params.verified,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
    }),
    [
      params.page,
      params.limit,
      params.sortBy,
      params.sortOrder,
      params.q,
      params.role,
      params.status,
      params.verified,
      params.dateFrom,
      params.dateTo,
    ],
  );

  // Stable effect that depends only on params and refetch trigger
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();

    // Create a key that includes the refetch trigger to force re-fetch when needed
    const key = JSON.stringify({ params: stableParams, trigger: refetchTrigger });
    if (inflightRef.current === key) {
      console.log("[useUsersQuery] Skipping duplicate in-flight request");
      return;
    }
    inflightRef.current = key;

    (async () => {
      try {
        setIsLoading(true);
        setIsError(false);
        setError(null);

        const response = await listUsers(stableParams, { signal: ctrl.signal });

        if (!cancelled && response?.data) {
          // Only update state if data actually changed
          setData((prev) => (shallowEqual(prev, response.data) ? prev : response.data));
        }
      } catch (err: any) {
        if (!cancelled && err.name !== "CanceledError" && err.name !== "AbortError") {
          setIsError(true);

          // Ensure error is always a string
          let errorMessage = "Failed to fetch users";
          if (err.response?.data?.error) {
            // Handle {error: "message"} format from backend
            errorMessage =
              typeof err.response.data.error === "string"
                ? err.response.data.error
                : JSON.stringify(err.response.data.error);
          } else if (err.response?.data?.message) {
            errorMessage =
              typeof err.response.data.message === "string"
                ? err.response.data.message
                : JSON.stringify(err.response.data.message);
          } else if (err.message) {
            errorMessage =
              typeof err.message === "string" ? err.message : JSON.stringify(err.message);
          }

          setError(errorMessage);
          setData(null);

          if (process.env.NODE_ENV !== "production") {
            // dev-only visibility
            console.warn("Error fetching users:", err);
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          // Clear in-flight ref after request completes
          inflightRef.current = null;
        }
      }
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
      // Clear in-flight ref on cleanup
      inflightRef.current = null;
    };
  }, [stableParams, refetchTrigger]);

  // Refetch function for manual refresh
  const refetch = () => {
    setRefetchTrigger((prev) => prev + 1);
  };

  return {
    data,
    isLoading,
    isError,
    error,
    params,
    setParams,
    clearFilters,
    hasActiveFilters: hasActiveFilters(),
    refetch,
  };
}
